import { type CharacterSummary } from '@d2/schema/characters';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import {
  type AccountApi,
  type ApiRefusal,
  type ApiResult,
  type Purchase,
  type QuotedThing,
  type Sale,
  type VendorWindowOffer,
} from '../account/api';
import {
  type ExchangeKind,
  type VendorNote,
  type VendorPending,
  bought,
  repaired,
  sold,
  startExchange,
  vendorRefused,
} from './vendor-model';
import { VendorPanel } from './vendor-panel';
import { vendorViewOf } from './vendor-view';

/**
 * The vendor's counter: the one thing in town that talks to the API.
 *
 * `vendor-panel.tsx` draws and decides nothing; `vendor-view.ts` arranges;
 * `vendor-model.ts` owns the words and the latch. What is left — two reads, a
 * promise that may land after the component is gone, and one exchange at a time
 * — is genuinely React's job, and it lives here rather than in `AccountGate.tsx`
 * for one concrete reason: **this component exists exactly as long as town is on
 * screen**. Descending unmounts it and coming back mounts a new one, so a
 * character who has just been down a floor is read again without anybody having
 * to remember to invalidate anything.
 *
 * ## Two reads, and neither of them is a snapshot
 *
 * The **window** is what this character may buy, and it changes when they level.
 * The **quote** is what the vendor would pay for what they carry and what mending
 * it would cost, and it changes after every exchange. Both are the API's answers
 * to questions it alone can price: this component reads them and holds them, and
 * there is no third thing to fetch — it used to read the whole character
 * snapshot and price the pack itself with `@d2/core`, and that was the last rule
 * in this client.
 *
 * ## The purse and the counter are both handed up, never worked out
 *
 * A successful exchange answers with the character **as it now stands** *and*
 * with the fresh quote, so `onCharacter` hands town a replacement for the row it
 * draws its gold bar from and the counter redraws from the same answer in the
 * same render. Nothing here subtracts a price from a balance, and there is
 * deliberately no balance in scope to subtract one from: this component never
 * reads `character.gold` at all. That is the same rule the death toll is held to
 * one file over — *"the server's answer is the fact, and the client's copy is
 * older than it thinks"* — and the seam is tighter here, because the answer to
 * the very request that spent the gold carries both new facts.
 *
 * Re-reading after an exchange would be asking a second time for something
 * already in hand, and the second answer is the later — and therefore more
 * easily raced — of the two.
 *
 * ## One exchange at a time, latched twice
 *
 * `pending` is state, because the panel draws from it. The **ref** beside it is
 * the latch that actually protects the money: state is batched, and two clicks
 * dispatched inside one flush would both read the same `null`. The ref is
 * written synchronously, before the request goes out, so the second press
 * decides against a value the first has already set.
 *
 * Neither replaces the panel's `disabled` control, and none of the three is
 * redundant: the disabled button stops the press, the ref stops the request, and
 * `startExchange` is the decision both of them ask.
 */

export interface VendorCounterProps {
  readonly api: AccountApi;
  readonly token: string;
  readonly characterId: string;
  /**
   * The character as the server now has it.
   *
   * Called on every successful exchange and never on a refusal: nothing moved,
   * so there is nothing to say about the character that the row town already
   * holds does not say. Town redraws its purse from this.
   */
  readonly onCharacter: (character: CharacterSummary) => void;
  /** The token is no longer accepted. The shell owns what happens next. */
  readonly onSessionEnded: () => void;
}

export const VendorCounter = ({
  api,
  token,
  characterId,
  onCharacter,
  onSessionEnded,
}: VendorCounterProps): ReactElement => {
  const [stock, setStock] = useState<readonly VendorWindowOffer[] | null>(null);
  const [quote, setQuote] = useState<readonly QuotedThing[] | null>(null);
  const [failure, setFailure] = useState<ApiRefusal | null>(null);
  const [pending, setPending] = useState<VendorPending | null>(null);
  const [note, setNote] = useState<VendorNote | null>(null);

  /** True while this component is mounted. An answer may land after a descent. */
  const live = useRef(true);
  /** The latch that protects the money. See the header for why the state is not it. */
  const inFlight = useRef<VendorPending | null>(null);

  useEffect(() => {
    live.current = true;

    return (): void => {
      live.current = false;
    };
  }, []);

  /**
   * A refusal, routed.
   *
   * A 401 is not a vendor problem and must not be drawn as one — it is the
   * session ending, which is the shell's business. Everything else is something
   * to say at the counter.
   */
  const refused = useCallback(
    (refusal: ApiRefusal, say: (refusal: ApiRefusal) => void): void => {
      if (refusal.kind === 'unauthenticated') {
        onSessionEnded();

        return;
      }

      say(refusal);
    },
    [onSessionEnded],
  );

  // The window and the quote, once per visit to town — which is once per mount,
  // because this component only exists while town is on screen. Every exchange
  // after that carries its own new quote.
  useEffect(() => {
    void (async (): Promise<void> => {
      const [window, quoted] = await Promise.all([
        api.readVendorStock(token, characterId),
        api.readVendorQuote(token, characterId),
      ]);
      if (!live.current) return;

      if (!window.ok) {
        refused(window.refusal, setFailure);

        return;
      }
      if (!quoted.ok) {
        refused(quoted.refusal, setFailure);

        return;
      }

      setStock(window.value);
      setQuote(quoted.value);
      setFailure(null);
    })();
  }, [api, characterId, refused, token]);

  /**
   * One exchange, from the press to the sentence.
   *
   * `name` is this client's — it knows what it pressed — and every **figure** in
   * the note is the answer's. If the two ever disagree about a price, the
   * server's is the one that was charged.
   */
  const exchange = useCallback(
    <T extends Purchase | Sale>(
      kind: ExchangeKind,
      id: string,
      ask: () => Promise<ApiResult<T>>,
      noted: (value: T) => VendorNote,
    ): void => {
      const started = startExchange(inFlight.current, { kind, id });
      // A second press while one is in flight. The API is atomic, so what this
      // protects is the player's gold rather than the database.
      if (started === null) return;

      inFlight.current = started;
      setPending(started);
      setNote(null);

      void (async (): Promise<void> => {
        const result = await ask();
        inFlight.current = null;
        if (!live.current) return;

        setPending(null);

        if (!result.ok) {
          refused(result.refusal, (refusal) => {
            setNote(vendorRefused(refusal));
          });

          return;
        }

        // The whole new state, used rather than re-fetched: the counter and the
        // row town's purse is drawn from, both out of the one answer, in the one
        // render that also says what was charged.
        setQuote(result.value.quote);
        onCharacter(result.value.character);
        setNote(noted(result.value));
      })();
    },
    [onCharacter, refused],
  );

  /**
   * What the thing an exchange named is called, for the sentence about it.
   *
   * Read off the state the panel was drawn from, because that is the list the
   * player pressed. `it` is unreachable in practice — an id only exists because
   * a row carrying it was rendered — and is a fallback rather than a throw for
   * the reason every other refusal here is: a sentence that reads a little
   * vaguer beats a screen that has fallen over on the way to saying it.
   */
  const nameOf = useCallback(
    (kind: ExchangeKind, id: string): string => {
      if (kind === 'buy') return stock?.find((offer) => offer.offerId === id)?.name ?? 'it';

      return quote?.find((row) => row.itemId === id)?.name ?? 'it';
    },
    [quote, stock],
  );

  const onBuy = useCallback(
    (offerId: string): void => {
      const name = nameOf('buy', offerId);

      exchange(
        'buy',
        offerId,
        async () => api.buyFromVendor(token, characterId, offerId),
        (purchase) => bought(name, purchase.spent),
      );
    },
    [api, characterId, exchange, nameOf, token],
  );

  const onSell = useCallback(
    (itemId: string): void => {
      const name = nameOf('sell', itemId);

      exchange(
        'sell',
        itemId,
        async () => api.sellToVendor(token, characterId, itemId),
        (sale) => sold(name, sale.received),
      );
    },
    [api, characterId, exchange, nameOf, token],
  );

  const onRepair = useCallback(
    (itemId: string): void => {
      const name = nameOf('repair', itemId);

      exchange(
        'repair',
        itemId,
        async () => api.repairAtVendor(token, characterId, itemId),
        (paid) => repaired(name, paid.spent),
      );
    },
    [api, characterId, exchange, nameOf, token],
  );

  return (
    <VendorPanel
      // Composed on every render rather than remembered: it is a pure function
      // of two pieces of state, and a memo of it would be a third place for the
      // counter and the character to fall out of step.
      view={stock === null || quote === null ? null : vendorViewOf(stock, quote)}
      failure={failure}
      onBuy={onBuy}
      onSell={onSell}
      onRepair={onRepair}
      pending={pending}
      note={note}
    />
  );
};
